// @greenlight-spec: 28-it-dashboard-ui.md#compliance--soc-2-evidence
// @greenlight-spec: 10-security-compliance.md#compliance-evidence-not-compliance-bundles
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link } from 'react-router-dom';

import { buildHipaaCsv, buildHipaaJson } from '@/components/compliance/exportHipaa';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Button } from '@/components/ui/button';
import { downloadTextFile } from '@/lib/download';
import type {
  BaaRecord,
  HipaaControlRow,
  HipaaEvidence,
  PhiIntegrationEvidence,
} from '@greenlight/contract';

const SAFEGUARD_LABEL: Record<HipaaControlRow['safeguard'], string> = {
  administrative: 'Administrative safeguards',
  technical: 'Technical safeguards',
};

/**
 * Every non-active state reads as a problem, because each one is a finding against the
 * BAA gate. They stay separately worded so an operator knows whether they are chasing a
 * renewal, a revocation, or paperwork that was never filed.
 */
const BAA_LABEL: Record<BaaRecord['state'], string> = {
  active: 'BAA on file',
  expired: 'BAA expired',
  revoked: 'BAA revoked',
  missing: 'No BAA on file',
};

function baaVariant(state: BaaRecord['state']): StatusBadgeVariant {
  switch (state) {
    case 'active':
      return 'active';
    case 'revoked':
      return 'revoked';
    // Expired and never-filed are both a missing agreement, so both read as a failure
    // rather than a warning — the gate does not distinguish them either.
    case 'expired':
    case 'missing':
      return 'failed';
  }
}

function ControlRow({ row }: { readonly row: HipaaControlRow }): React.JSX.Element {
  return (
    <li className="flex flex-col gap-2 py-4 md:flex-row md:gap-6">
      <div className="md:w-44 md:shrink-0">
        <MonoValue>{row.control}</MonoValue>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-foreground">{row.title}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{row.mechanism}</p>
        {row.policy_ids.length > 0 && (
          <p className="mt-2 flex flex-wrap gap-1.5">
            {row.policy_ids.map((id) => (
              <MonoValue key={id}>{id}</MonoValue>
            ))}
          </p>
        )}
      </div>
      <div className="flex items-start gap-2 md:w-44 md:shrink-0 md:justify-end">
        <StatusBadge variant={row.coverage === 'enforced' ? 'active' : 'inactive'}>
          {row.coverage === 'enforced'
            ? 'Enforced at merge'
            : row.coverage === 'reported'
              ? 'Reported, not blocking'
              : 'Recorded'}
        </StatusBadge>
        <Link
          to={row.evidence_href}
          className="text-[12px] font-medium text-link underline-offset-4 hover:underline"
        >
          Evidence
        </Link>
      </div>
    </li>
  );
}

function PhiRow({ phi }: { readonly phi: PhiIntegrationEvidence }): React.JSX.Element {
  const { baa } = phi;
  return (
    <li className="flex flex-col gap-1.5 py-3 md:flex-row md:items-center md:gap-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{phi.display_name}</p>
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          <MonoValue>{phi.name}</MonoValue>
          {baa.counterparty !== null && <> · {baa.counterparty}</>}
          {baa.expires_at !== null && (
            <> · expires {new Date(baa.expires_at).toLocaleDateString()}</>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 md:w-64 md:justify-end">
        <StatusBadge variant={baaVariant(baa.state)}>{BAA_LABEL[baa.state]}</StatusBadge>
        {baa.state !== 'active' && (
          <span className="text-[12px] text-muted-foreground">
            {phi.grant_blocked ? 'Grants refused' : 'Reported, not blocking'}
          </span>
        )}
      </div>
    </li>
  );
}

/**
 * The HIPAA half of the compliance view.
 *
 * Renders nothing when no integration is tagged for PHI, which is every org that does
 * not handle it. HIPAA is not a roadmap marker: an org with no PHI sees no HIPAA
 * heading, no empty state, and no disabled tile.
 */
export function HipaaSection({
  evidence,
}: {
  readonly evidence: HipaaEvidence;
}): React.JSX.Element | null {
  if (evidence.phi_integrations.length === 0) return null;

  const safeguards: HipaaControlRow['safeguard'][] = ['administrative', 'technical'];
  const download = (format: 'json' | 'csv'): void => {
    const stamp = evidence.generated_at.slice(0, 10);
    if (format === 'json') {
      downloadTextFile(
        `greenlight-hipaa-${stamp}.json`,
        buildHipaaJson(evidence),
        'application/json',
      );
    } else {
      downloadTextFile(`greenlight-hipaa-${stamp}.csv`, buildHipaaCsv(evidence), 'text/csv');
    }
  };

  return (
    <div className="space-y-5" data-testid="hipaa-section">
      <SectionCard title="HIPAA Security Rule">
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          Safeguard coverage for the integrations put in PHI scope. Coverage and evidence, not an
          attestation of HIPAA compliance.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" className="h-11 md:h-9" onClick={() => download('json')}>
            Export HIPAA JSON
          </Button>
          <Button variant="outline" className="h-11 md:h-9" onClick={() => download('csv')}>
            Export HIPAA CSV
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="PHI integrations">
        <ul className="divide-y divide-border">
          {evidence.phi_integrations.map((phi) => (
            <PhiRow key={phi.integration_id} phi={phi} />
          ))}
        </ul>
      </SectionCard>

      {safeguards.map((safeguard) => {
        const rows = evidence.controls.filter((c) => c.safeguard === safeguard);
        if (rows.length === 0) return null;
        return (
          <SectionCard key={safeguard}>
            <Eyebrow>{SAFEGUARD_LABEL[safeguard]}</Eyebrow>
            <ul className="mt-2 divide-y divide-border">
              {rows.map((row) => (
                <ControlRow key={row.control} row={row} />
              ))}
            </ul>
          </SectionCard>
        );
      })}
    </div>
  );
}
